import { useMemo, useState } from 'react';
import Button from '@mui/material/Button';

import EmptyView from 'Lib/components/common/table/EmptyView';
import { PdfViewer } from 'Lib/components/PdfViewer/PdfViewer';

import ZoomableMediaContainer from './ZoomableMediaContainer';

/**
 * Thin wrapper around ZoomableMediaContainer that renders a paginated PDF controlled by Zoomable controls.
 * @typedef {Object} ZoomablePDFProps
 * @property {string} src the pdf src
 * @returns a zoomable paginated PDF component
 */

/**
 * @typedef {function} ZoomablePDF
 * @param {ZoomablePDFProps} param  
 * @returns {JSX.Element} The PDF viewer component.

 */
export default function ZoomablePDF({ src }) {
    const [pageNumber, setPageNumber] = useState(1);
    const [numPages, setNumPages] = useState(1);

    // ShowDocument is used to toggle between the password protected view and the PDF view.
    // State is here so the empty view can replace the Zoomable viewer instead of rendering inside it.
    const [showDocument, setShowDocument] = useState(true);

    // Memoize pageNumber to pipe the reactive value to actionIcons.
    const memoPageNum = useMemo(() => pageNumber, [pageNumber]);

    const handleDocumentLoaded = numPages => {
        setNumPages(numPages);
    };

    const handlePageLoaded = (setMediaIsLoaded, centerView) => {
        setMediaIsLoaded(true);
        centerView(1, 0);
    };

    const actionIcons = [
        {
            tooltipTitle: memoPageNum < numPages ? 'Next PDF Page' : 'End of Document',
            onClick: () => {
                if (memoPageNum < numPages) setPageNumber(prevState => prevState + 1);
            },
            variation: memoPageNum < numPages ? 'enabled' : 'disabled',
            icon: 'chevron_right'
        },
        {
            tooltipTitle: memoPageNum > 1 ? 'Previous PDF Page' : 'Beginning of Document',
            onClick: () => {
                if (memoPageNum > 1) setPageNumber(prevState => prevState - 1);
            },
            variation: memoPageNum > 1 ? 'enabled' : 'disabled',
            icon: 'chevron_left'
        }
    ];

    return !showDocument ? (
        <EmptyView grow title="PDF Password Protected" subTitle="A Password is Required to Open this Document.">
            <Button
                onClick={() => {
                    setShowDocument(true);
                }}
            >
                Enter Password & View PDF
            </Button>
        </EmptyView>
    ) : (
        <ZoomableMediaContainer actions={actionIcons}>
            {({ setMediaIsLoaded, centerView }) => {
                return (
                    <PdfViewer
                        src={src}
                        setShowDocument={setShowDocument}
                        pageNumber={pageNumber}
                        handlePageLoaded={() => handlePageLoaded(setMediaIsLoaded, centerView)}
                        handleDocumentLoaded={handleDocumentLoaded}
                        numPages={numPages}
                    />
                );
            }}
        </ZoomableMediaContainer>
    );
}
